/**
 * Top-level error boundary fallback (#147).
 *
 * The #145 blank-screen incident happened because auth init threw and
 * nothing caught it — the React shell never rendered. This fallback is
 * what would have rendered instead. Dependency-free, no router, no
 * context — assumes everything below it might be broken.
 */

import * as Sentry from '@sentry/react';

export function SentryFallback({
  error,
  resetError,
  eventId,
}: {
  error: unknown;
  resetError: () => void;
  eventId: string;
}) {
  const message =
    error instanceof Error ? error.message : 'An unexpected error occurred';

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#0a0a0a',
        color: '#e5e5e5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        fontFamily:
          'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <div style={{ maxWidth: '480px', textAlign: 'center' }}>
        <h1 style={{ fontSize: '1.5rem', marginBottom: '0.75rem' }}>
          Something went wrong
        </h1>
        <p style={{ color: '#a3a3a3', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
          The launcher hit an error and couldn&rsquo;t render. We&rsquo;ve been
          notified.
        </p>
        <details
          style={{
            textAlign: 'left',
            background: '#171717',
            padding: '0.75rem 1rem',
            borderRadius: '6px',
            marginBottom: '1.5rem',
            fontSize: '0.8rem',
            color: '#fca5a5',
            overflowWrap: 'break-word',
          }}
        >
          <summary style={{ cursor: 'pointer', color: '#a3a3a3' }}>
            Error detail
          </summary>
          <pre style={{ margin: '0.5rem 0 0', whiteSpace: 'pre-wrap' }}>
            {message}
          </pre>
          {eventId ? (
            <p style={{ color: '#6b7280', marginTop: '0.5rem', fontSize: '0.75rem' }}>
              Reference: {eventId}
            </p>
          ) : null}
        </details>
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
          <button
            type="button"
            onClick={resetError}
            style={{
              background: '#2563eb',
              color: 'white',
              border: 'none',
              padding: '0.6rem 1.2rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem',
            }}
          >
            Try again
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              background: '#374151',
              color: 'white',
              border: 'none',
              padding: '0.6rem 1.2rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem',
            }}
          >
            Reload page
          </button>
        </div>
      </div>
    </div>
  );
}

// Convenience export so main.tsx stays terse.
export { Sentry };
